import React, { useState } from "react";
import SEO from "../../components/common/SEO";
import {
  Code,
  Copy,
  CheckCircle,
  Terminal,
  Zap,
  Shield,
  Globe,
  BookOpen,
  Key,
  Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const API_EXAMPLES = {
  javascript: {
    single: `const response = await fetch('${API_BASE}/validate-api-email', {
  method: 'POST',
  headers: {
    'Authorization': 'ApiKey YOUR_API_SECRET',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: 'user@example.com',
    include_ai_analysis: true
  })
});

const result = await response.json();
const creditsRemaining = response.headers.get('X-Credits-Remaining');
console.log(result, creditsRemaining);`,
    bulk: `const response = await fetch('${API_BASE}/validate-api-bulk', {
  method: 'POST',
  headers: {
    'Authorization': 'ApiKey YOUR_API_SECRET',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    emails: [
      'luffy@onepiece.com',
      'zoro@onepiece.com',
      'sanji@baratie.com'
    ],
    include_ai_analysis: true
  })
});

const results = await response.json();
console.log(results);`,
    upload: `const form = new FormData();
form.append('file', fileInput.files[0]);

const response = await fetch('${API_BASE}/upload-api-csv', {
  method: 'POST',
  headers: {
    'Authorization': 'ApiKey YOUR_API_SECRET'
  },
  body: form
});

const results = await response.json();
console.log(results);`,
  },
  axios: {
    single: `await axios.post(
  '${API_BASE}/validate-api-email',
  {
    email: 'test@example.com',
    include_ai_analysis: true
  },
  { 
    headers: { 
      Authorization: 'ApiKey YOUR_API_SECRET' 
    } 
  }
);`,
    bulk: `await axios.post(
  '${API_BASE}/validate-api-bulk',
  {
    emails: [
      'luffy@onepiece.com',
      'zoro@onepiece.com'
    ],
    include_ai_analysis: true
  },
  { 
    headers: { 
      Authorization: 'ApiKey YOUR_API_SECRET' 
    } 
  }
);`,
    upload: `const form = new FormData();
form.append('file', fileInput.files[0]);

await axios.post(
  '${API_BASE}/upload-api-csv',
  form,
  {
    headers: {
      Authorization: 'ApiKey YOUR_API_SECRET'
    }
  }
);`,
  },
  python: {
    single: `import requests

url = '${API_BASE}/validate-api-email'
headers = {
    'Authorization': 'ApiKey YOUR_API_SECRET',
    'Content-Type': 'application/json'
}
data = {
    'email': 'user@example.com',
    'include_ai_analysis': True
}

response = requests.post(url, headers=headers, json=data)
result = response.json()
print(result)`,
    bulk: `import requests

url = '${API_BASE}/validate-api-bulk'
headers = {
    'Authorization': 'ApiKey YOUR_API_SECRET',
    'Content-Type': 'application/json'
}
data = {
    'emails': [
        'luffy@onepiece.com',
        'zoro@onepiece.com'
    ],
    'include_ai_analysis': True
}

response = requests.post(url, headers=headers, json=data)
print(response.json())`,
    upload: `import requests

url = '${API_BASE}/upload-api-csv'
headers = {
    'Authorization': 'ApiKey YOUR_API_SECRET'
}
files = {
    'file': open('emails.csv', 'rb')
}

response = requests.post(url, headers=headers, files=files)
print(response.json())`,
  },
  curl: {
    single: `curl -X POST ${API_BASE}/validate-api-email \\
  -H "Authorization: ApiKey YOUR_API_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "include_ai_analysis": true
  }'`,
    bulk: `curl -X POST ${API_BASE}/validate-api-bulk \\
  -H "Authorization: ApiKey YOUR_API_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "emails": [
      "luffy@onepiece.com",
      "zoro@onepiece.com"
    ],
    "include_ai_analysis": true
  }'`,
    upload: `curl -X POST ${API_BASE}/upload-api-csv \\
  -H "Authorization: ApiKey YOUR_API_SECRET" \\
  -F "file=@emails.csv"`,
  },
};

const RESPONSE_EXAMPLE = `{
  "email": "user@example.com",
  "is_valid": true,
  "confidence_score": 0.95,
  "risk_level": "low",
  "validation_details": {
    "syntax_valid": true,
    "dns_valid": true,
    "domain": "example.com",
    "is_disposable": false,
    "syntax_errors": [],
    "dns_errors": []
  },
  "ai_analysis": "High confidence..."
}`;

export default function ApiDocs({ user, authToken }) {
  const [selectedLanguage, setSelectedLanguage] = useState("javascript");
  const [selectedEndpoint, setSelectedEndpoint] = useState("single");
  const [copiedCode, setCopiedCode] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const currentCode = API_EXAMPLES[selectedLanguage][selectedEndpoint];

  return (
    <>
      <SEO
        title="API Documentation - VALIDMAIL"
        description="Complete API documentation for email validation. RESTful API with support for Node.js, Python, PHP, and more."
        keywords="email validation API, REST API, developer API"
        canonicalUrl="https://validmail.io/product/api"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              Developer API
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6 px-4">
              Build with our{" "}
              <span className="text-emerald-600 dark:text-emerald-400">
                powerful API
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto px-4">
              RESTful API for email validation. Simple integration with any tech stack.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-200 dark:border-emerald-800">
              <Zap className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 dark:text-emerald-400 mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-50 mb-1 sm:mb-2">
                Lightning Fast
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                Response time under 200ms. Built for high-performance apps.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200 dark:border-blue-800">
              <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600 dark:text-blue-400 mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-50 mb-1 sm:mb-2">
                Secure & Reliable
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                Enterprise-grade security with API key authentication.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200 dark:border-purple-800 sm:col-span-2 md:col-span-1">
              <Globe className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600 dark:text-purple-400 mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-50 mb-1 sm:mb-2">
                Easy Integration
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                Simple REST API works with any language and framework.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-12 md:mb-16">
            <div className="lg:col-span-2 space-y-6 lg:space-y-8">
              <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border-2 border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-2">
                      <Terminal className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span className="hidden sm:inline">Quick Start</span>
                      <span className="sm:hidden">Code</span>
                    </h3>
                    <button
                      onClick={() => copyToClipboard(currentCode)}
                      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium transition-all text-xs sm:text-sm"
                    >
                      {copiedCode ? (
                        <>
                          <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                          <span className="hidden sm:inline">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">Copy</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-x-auto pb-2">
                    {["javascript", "axios", "python", "curl"].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                          selectedLanguage === lang
                            ? "bg-emerald-600 text-white shadow-lg"
                            : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600"
                        }`}
                      >
                        {lang === "javascript"
                          ? "Fetch"
                          : lang === "axios"
                          ? "Axios"
                          : lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2">
                    <button
                      onClick={() => setSelectedEndpoint("single")}
                      className={`flex-1 min-w-[80px] px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                        selectedEndpoint === "single"
                          ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-md"
                          : "text-slate-600 dark:text-slate-400 hover:bg-white/50 dark:hover:bg-slate-900/50"
                      }`}
                    >
                      Single
                    </button>
                    <button
                      onClick={() => setSelectedEndpoint("bulk")}
                      className={`flex-1 min-w-[80px] px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                        selectedEndpoint === "bulk"
                          ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-md"
                          : "text-slate-600 dark:text-slate-400 hover:bg-white/50 dark:hover:bg-slate-900/50"
                      }`}
                    >
                      Bulk
                    </button>
                    <button
                      onClick={() => setSelectedEndpoint("upload")}
                      className={`flex-1 min-w-[80px] px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                        selectedEndpoint === "upload"
                          ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-md"
                          : "text-slate-600 dark:text-slate-400 hover:bg-white/50 dark:hover:bg-slate-900/50"
                      }`}
                    >
                      CSV
                    </button>
                  </div>
                </div>

                <div className="p-4 sm:p-6 bg-slate-900 dark:bg-slate-950 overflow-x-auto">
                  <pre className="text-xs sm:text-sm text-emerald-400">
                    <code>{currentCode}</code>
                  </pre>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border-2 border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 flex items-center gap-2">
                    <Code className="h-4 w-4 sm:h-5 sm:w-5" />
                    Response Format
                  </h3>
                </div>

                <div className="p-4 sm:p-6 bg-slate-900 dark:bg-slate-950 overflow-x-auto">
                  <pre className="text-xs sm:text-sm text-sky-400">
                    <code>{RESPONSE_EXAMPLE}</code>
                  </pre>
                </div>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-xl border-2 border-slate-200 dark:border-slate-800 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <Key className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-50">
                    Get API Key
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-3 sm:mb-4">
                  Sign up to receive your API key instantly. Start with 100 free credits.
                </p>
                <button
                  onClick={() => window.location.href = authToken ? "/profile" : "/signin"}
                  className="w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl"
                >
                  {authToken ? "View API Key" : "Get Started"}
                </button>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-xl border-2 border-slate-200 dark:border-slate-800 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-50">
                    Endpoints
                  </h3>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div className="p-2 sm:p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <div className="text-[10px] sm:text-xs font-mono text-emerald-600 dark:text-emerald-400 mb-1 break-all">
                      POST /api/validate-api-email
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400">
                      Validate single email
                    </p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <div className="text-[10px] sm:text-xs font-mono text-emerald-600 dark:text-emerald-400 mb-1 break-all">
                      POST /api/validate-api-bulk
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400">
                      Validate up to 1000 emails
                    </p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <div className="text-[10px] sm:text-xs font-mono text-emerald-600 dark:text-emerald-400 mb-1 break-all">
                      POST /api/upload-api-csv
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-600 dark:text-slate-400">
                      Upload CSV/XLSX/XLS files
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl border-2 border-sky-200 dark:border-sky-800 p-4 sm:p-6">
                <h4 className="text-sm sm:text-base font-bold text-sky-900 dark:text-sky-300 mb-2 sm:mb-3">
                  Authentication
                </h4>
                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 mb-2">
                  Use either header:
                </p>
                <pre className="text-[10px] sm:text-xs bg-white dark:bg-slate-900 p-2 sm:p-3 rounded-lg font-mono text-slate-900 dark:text-slate-50 overflow-x-auto">
{`Authorization: ApiKey YOUR_SECRET

# or

X-API-KEY: YOUR_SECRET`}
                </pre>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl border-2 border-amber-200 dark:border-amber-800 p-4 sm:p-6">
                <h4 className="text-sm sm:text-base font-bold text-amber-900 dark:text-amber-300 mb-2">
                  Limits
                </h4>
                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                  Max 1000 emails/request<br/>
                  Credits per email validated
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">
              Need help?
            </h2>
            <p className="text-base sm:text-xl mb-6 sm:mb-8 opacity-90">
              Check your profile for API credentials or contact support.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => window.location.href = authToken ? "/profile" : "/signin"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold bg-white text-emerald-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl"
              >
                {authToken ? "View Credentials" : "Get Started"}
              </button>
              <button
                onClick={() => window.location.href = "mailto:support@validmail.io"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold border-2 border-white text-white hover:bg-white hover:text-emerald-600 transition-all"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
